import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import api from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = signed-in
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("dk_token");
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem("dk_token");
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    return data; // { mfa_required, mfa_token, enrol, otpauth_uri, secret }
  }, []);

  const verifyMfa = useCallback(async (mfa_token, code, method = "totp") => {
    const { data } = await api.post("/auth/mfa/verify", { mfa_token, code, method });
    if (data.enrol) return { enrol: data };
    localStorage.setItem("dk_token", data.access_token);
    setUser(data.user);
    return data.user;
  }, []);

  const can = useCallback((perm) => {
    if (!user || !user.permissions) return false;
    const [m, a] = perm.split(".");
    if (user.permissions.includes(perm) || user.permissions.includes(`${m}.manage`)) return true;
    return a === "view" && user.permissions.some((p) => p.startsWith(`${m}.`));
  }, [user]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("dk_token");
    setUser(false);
  }, []);

  const value = useMemo(() => ({ user, loading, login, verifyMfa, can, logout, refresh }), [user, loading, login, verifyMfa, can, logout, refresh]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
